// ============================================
// OFFICEFORGED — PDF Viewer & Editor Module
// Full PDF suite: view, annotate, sign, merge,
// split, rotate, search, print, download.
// Uses PDF.js (render) + pdf-lib (manipulate)
// ============================================

const pdfjsLib = () => window.pdfjsLib;
const PDFLib = () => window.PDFLib;

export class PDFViewer {
  constructor() {
    this.pdfDoc = null;        // PDF.js document object
    this.pdfBytes = null;      // Raw Uint8Array for pdf-lib
    this.currentPage = 1;
    this.totalPages = 0;
    this.scale = 1.5;
    this.fileName = 'document.pdf';
    this.annotations = {};     // { pageNum: [{type, x, y, data}] }
    this.signatures = [];      // saved signature images (data URLs)
    this.activeTool = null;    // 'highlight' | 'note' | 'draw' | 'sign' | null
    this.rotations = {};       // { pageNum: degrees }
    this._drawPaths = {};      // { pageNum: [ [{x,y},...] ] }
    this._isDrawing = false;
    this._currentPath = [];
    this._highlights = {};     // { pageNum: [{startOffset, endOffset, rects}] }
    this._notes = {};          // { pageNum: [{x, y, text}] }
    this._textAnnotations = {}; // { pageNum: [{x, y, text, fontSize, color, width}] }
    this._stampQueue = null;   // signature to stamp
    this._longPressTimer = null;
    this._textEdits = {};      // { pageNum: { itemIdx: { original, edited, entry } } }
    this._pageTextItems = {};  // { pageNum: [ {str, transform, ...} ] } — original PDF text
    this._pageEntries = {};    // { pageNum: [ { item, idx, fontSize, x, y, w } ] } — positioned items
    this._redactions = {};     // { pageNum: [{x, y, w, h}] }
    this._formFields = {};    // { pageNum: [ field elements ] }
    this._darkMode = false;
    this._reorderMode = false;
    this._pendingStamp = null; // stamp image data URL to place
    this._editTextMode = false;  // Toggle for edit-existing-text mode
    this._undoStack = [];        // Undo history
    this._redoStack = [];        // Redo history
    this._searchMatches = [];    // Current search result spans
    this._searchIdx = -1;        // Current search match index

    this._init();
  }

  _init() {
    // Set PDF.js worker
    if (window.pdfjsLib) {
      window.pdfjsLib.GlobalWorkerOptions.workerSrc =
        'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.worker.min.js';
    }
    this._setupToolbar();
    this._setupDropZone();
  }

  // ==========================================
  // TOOLBAR WIRING
  // ==========================================

  _setupToolbar() {
    this._wire('pdf-open', () => this._openFilePicker());
    this._wire('pdf-prev', () => this._goToPage(this.currentPage - 1));
    this._wire('pdf-next', () => this._goToPage(this.currentPage + 1));
    this._wire('pdf-zoom-in', () => this._setZoom(this.scale + 0.25));
    this._wire('pdf-zoom-out', () => this._setZoom(Math.max(0.5, this.scale - 0.25)));
    this._wire('pdf-search', () => this._searchDialog());
    this._wire('pdf-highlight', () => this._toggleTool('highlight'));
    this._wire('pdf-note', () => this._toggleTool('note'));
    this._wire('pdf-draw', () => this._toggleTool('draw'));
    this._wire('pdf-text', () => this._toggleTool('text'));
    this._wire('pdf-sign', () => this._showSignaturePad());
    this._wire('pdf-rotate', () => this._rotateCurrentPage());
    this._wire('pdf-merge', () => this._mergeDialog());
    this._wire('pdf-split', () => this._splitDialog());
    this._wire('pdf-delete-page', () => this._deleteCurrentPage());
    this._wire('pdf-edit-text', () => this._toggleEditTextMode());
    this._wire('pdf-stamp', () => this._showStampPicker());
    this._wire('pdf-redact', () => this._toggleTool('redact'));
    this._wire('pdf-undo', () => this._undo());
    this._wire('pdf-redo', () => this._redo());
    this._wire('pdf-form-fill', () => this._detectFormFields());
    this._wire('pdf-reorder', () => this._toggleReorderMode());
    this._wire('pdf-crop', () => this._cropDialog());
    this._wire('pdf-watermark', () => this._watermarkDialog());
    this._wire('pdf-page-numbers', () => this._addPageNumbers());
    this._wire('pdf-flatten', () => this._flattenAnnotations());
    this._wire('pdf-bookmarks', () => this._toggleBookmarks());
    this._wire('pdf-properties', () => this._showProperties());
    this._wire('pdf-dark-mode', () => this._toggleDarkMode());
    this._wire('pdf-present', () => this._presentationMode());
    this._wire('pdf-print', () => this._print());
    this._wire('pdf-download', () => this._download());

    const zoomSel = document.getElementById('pdf-zoom-select');
    if (zoomSel) {
      zoomSel.addEventListener('change', () => {
        const val = zoomSel.value;
        if (val === 'fit-width') this._fitWidth();
        else if (val === 'fit-page') this._fitPage();
        else this._setZoom(parseFloat(val));
      });
    }

    // Page number input
    const pageInput = document.getElementById('pdf-page-input');
    if (pageInput) {
      pageInput.addEventListener('change', () => {
        const num = parseInt(pageInput.value);
        if (num >= 1 && num <= this.totalPages) this._goToPage(num);
      });
      pageInput.addEventListener('keydown', (e) => {
        if (e.key === 'Enter') {
          const num = parseInt(pageInput.value);
          if (num >= 1 && num <= this.totalPages) this._goToPage(num);
        }
      });
    }
  }

  _wire(id, handler) {
    const el = document.getElementById(id);
    if (el) el.addEventListener('click', handler);
  }

  _setupDropZone() {
    const viewer = document.getElementById('pdf-viewer-container');
    if (!viewer) return;

    viewer.addEventListener('dragover', (e) => {
      e.preventDefault();
      viewer.classList.add('pdf-drag-over');
    });
    viewer.addEventListener('dragleave', () => {
      viewer.classList.remove('pdf-drag-over');
    });
    viewer.addEventListener('drop', (e) => {
      e.preventDefault();
      viewer.classList.remove('pdf-drag-over');
      const file = e.dataTransfer?.files?.[0];
      if (file && file.type === 'application/pdf') this._loadFile(file);
    });
  }

  // ==========================================
  // LOAD & RENDER
  // ==========================================

  _openFilePicker() {
    const input = document.getElementById('pdf-file-input');
    if (!input) return;
    input.value = '';
    input.onchange = () => {
      const file = input.files?.[0];
      if (file) this._loadFile(file);
    };
    input.click();
  }

  async _loadFile(file) {
    this.fileName = file.name;
    const buffer = await file.arrayBuffer();
    this.pdfBytes = new Uint8Array(buffer);
    await this._loadPDF(buffer);
  }

  async _loadPDF(arrayBuffer) {
    const lib = pdfjsLib();
    if (!lib) { alert('PDF.js not loaded. Check your internet connection.'); return; }

    try {
      this.pdfDoc = await lib.getDocument({ data: arrayBuffer }).promise;
      this.totalPages = this.pdfDoc.numPages;
      this.currentPage = 1;
      this.annotations = {};
      this.rotations = {};
      this._drawPaths = {};
      this._highlights = {};
      this._notes = {};
      this._textEdits = {};
      this._pageTextItems = {};
      this._pageEntries = {};
      this._redactions = {};
      this._formFields = {};

      this._updatePageInfo();
      this._hideEmptyState();
      await this._renderAllPages();
      this._generateThumbnails();
      this._updateFilename();

      // Auto-detect form fields and load bookmarks if panel open
      this._detectFormFields();
      const bmPanel = document.getElementById('pdf-bookmarks-panel');
      if (bmPanel && bmPanel.style.display !== 'none') this._loadBookmarks();
    } catch (e) {
      alert('Failed to load PDF: ' + e.message);
    }
  }

  async _renderAllPages() {
    const viewer = document.getElementById('pdf-viewer');
    if (!viewer || !this.pdfDoc) return;
    viewer.innerHTML = '';

    for (let i = 1; i <= this.totalPages; i++) {
      await this._renderPage(i, viewer);
    }
  }

  async _renderPage(pageNum, container) {
    const page = await this.pdfDoc.getPage(pageNum);
    const rotation = this.rotations[pageNum] || 0;
    const viewport = page.getViewport({ scale: this.scale, rotation });
    const lib = window.pdfjsLib;

    // Page wrapper
    const pageDiv = document.createElement('div');
    pageDiv.className = 'pdf-page';
    pageDiv.dataset.page = pageNum;
    pageDiv.style.width = viewport.width + 'px';
    pageDiv.style.height = viewport.height + 'px';

    // Render canvas
    const canvas = document.createElement('canvas');
    canvas.width = viewport.width * 2; // 2x for retina
    canvas.height = viewport.height * 2;
    canvas.style.width = viewport.width + 'px';
    canvas.style.height = viewport.height + 'px';
    const ctx = canvas.getContext('2d');
    ctx.scale(2, 2);

    await page.render({ canvasContext: ctx, viewport }).promise;
    pageDiv.appendChild(canvas);

    // Text layer — invisible text spans over canvas for selection & editing
    const textContent = await page.getTextContent();
    const textDiv = document.createElement('div');
    textDiv.className = 'pdf-text-layer';
    textDiv.style.width = viewport.width + 'px';
    textDiv.style.height = viewport.height + 'px';
    pageDiv.appendChild(textDiv);

    // Append pageDiv to DOM early so we can measure span widths for scaleX
    container.appendChild(pageDiv);

    // Create per-item spans using PDF.js coordinate math
    this._pageTextItems[pageNum] = textContent.items;
    const entries = [];
    const spansToMeasure = [];

    textContent.items.forEach((item, idx) => {
      if (!item.str?.trim() || !item.transform) return;
      try {
        const tx = lib.Util.transform(viewport.transform, item.transform);
        // Vertical font height (tx[2],tx[3]) — this is what PDF.js uses for Y offset
        const fontHeight = Math.sqrt(tx[2] * tx[2] + tx[3] * tx[3]);
        const fontSize = Math.sqrt(tx[0] * tx[0] + tx[1] * tx[1]);
        // PDF text width in screen pixels
        const pdfWidth = item.width ? item.width * viewport.scale : 0;

        const span = document.createElement('span');
        span.textContent = item.str;
        span.dataset.page = pageNum;
        span.dataset.itemIdx = entries.length;
        span.style.position = 'absolute';
        span.style.left = tx[4] + 'px';
        span.style.top = (tx[5] - fontHeight) + 'px';
        span.style.fontSize = fontHeight + 'px';
        span.style.fontFamily = 'sans-serif';
        span.style.transformOrigin = '0% 0%';

        entries.push({ item, idx, fontSize });
        spansToMeasure.push({ span, pdfWidth });

        const edit = this._textEdits[pageNum]?.[entries.length - 1];
        if (edit) {
          span.textContent = edit.edited;
          span.classList.add('pdf-text-edited');
        }
        textDiv.appendChild(span);
      } catch (e) {}
    });

    // Phase 2: measure each span's rendered width, apply scaleX to match PDF
    // This is the critical step that makes horizontal alignment pixel-perfect
    spansToMeasure.forEach(({ span, pdfWidth }) => {
      if (pdfWidth > 0) {
        const naturalWidth = span.offsetWidth;
        if (naturalWidth > 0) {
          span.style.transform = `scaleX(${pdfWidth / naturalWidth})`;
        }
      }
    });

    this._pageEntries[pageNum] = entries;
    this._wireTextEditing(textDiv, pageNum);

    // Annotation overlay canvas
    const overlay = document.createElement('canvas');
    overlay.className = 'pdf-annotation-layer';
    overlay.width = viewport.width * 2;
    overlay.height = viewport.height * 2;
    overlay.style.width = viewport.width + 'px';
    overlay.style.height = viewport.height + 'px';
    overlay.style.pointerEvents = this.activeTool ? 'auto' : 'none';
    pageDiv.appendChild(overlay);

    // Notes container
    const notesDiv = document.createElement('div');
    notesDiv.className = 'pdf-notes-layer';
    pageDiv.appendChild(notesDiv);

    // Page number label
    const label = document.createElement('div');
    label.className = 'pdf-page-label';
    label.textContent = pageNum;
    pageDiv.appendChild(label);

    // Wire interaction events
    this._wirePageEvents(pageDiv, overlay, notesDiv, pageNum);

    // Redraw any existing annotations
    this._redrawAnnotations(pageNum, overlay);
    this._redrawNotes(pageNum, notesDiv);
    this._renderTextAnnotations(pageNum, pageDiv);
  }

  _wirePageEvents(pageDiv, overlay, notesDiv, pageNum) {
    const ctx = overlay.getContext('2d');
    ctx.scale(2, 2);

    overlay.addEventListener('mousedown', (e) => this._onPointerDown(e, pageNum, overlay));
    overlay.addEventListener('mousemove', (e) => this._onPointerMove(e, pageNum, overlay));
    overlay.addEventListener('mouseup', (e) => this._onPointerUp(e, pageNum, overlay, notesDiv));

    overlay.addEventListener('touchstart', (e) => {
      if (this.activeTool) {
        e.preventDefault();
        const touch = e.touches[0];
        this._onPointerDown(touch, pageNum, overlay);
      }
      // Track swipe start
      this._touchStartX = e.touches[0].clientX;
      this._touchStartY = e.touches[0].clientY;
    }, { passive: false });
    overlay.addEventListener('touchmove', (e) => {
      if (this.activeTool && this._isDrawing) {
        e.preventDefault();
        const touch = e.touches[0];
        this._onPointerMove(touch, pageNum, overlay);
      }
    }, { passive: false });
    overlay.addEventListener('touchend', (e) => {
      if (this.activeTool) {
        e.preventDefault();
        const touch = e.changedTouches[0];
        this._onPointerUp(touch, pageNum, overlay, notesDiv);
      }
      // Detect horizontal swipe
      const endX = e.changedTouches[0].clientX;
      const endY = e.changedTouches[0].clientY;
      const dx = endX - (this._touchStartX || 0);
      const dy = endY - (this._touchStartY || 0);
      if (Math.abs(dx) > 80 && Math.abs(dx) > Math.abs(dy) * 2 && !this.activeTool) {
        if (dx < 0) this._goToPage(this.currentPage + 1);  // swipe left = next
        else this._goToPage(this.currentPage - 1);          // swipe right = prev
      }
    });

    // Track scroll for current page
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting && entry.intersectionRatio > 0.5) {
          this.currentPage = pageNum;
          this._updatePageInfo();
          this._highlightThumbnail(pageNum);
        }
      });
    }, { threshold: 0.5 });
    observer.observe(pageDiv);
  }

  // ==========================================
  // POINTER / DRAWING EVENTS
  // ==========================================

  _getPos(e, canvas) {
    const rect = canvas.getBoundingClientRect();
    return { x: e.clientX - rect.left, y: e.clientY - rect.top };
  }

  _onPointerDown(e, pageNum, canvas) {
    if (this.activeTool === 'draw') {
      this._isDrawing = true;
      this._currentPath = [this._getPos(e, canvas)];
    } else if (this.activeTool === 'redact') {
      this._isDrawing = true;
      this._redactStart = this._getPos(e, canvas);
    } else if (this.activeTool === 'sign' && this._stampQueue) {
      const pos = this._getPos(e, canvas);
      this._stampSignature(pageNum, pos, canvas);
    } else if (this.activeTool === 'stamp' && this._pendingStamp) {
      const pos = this._getPos(e, canvas);
      this._placeStamp(pageNum, pos, canvas);
    }
    // Long-press detection for editing existing text annotations
    clearTimeout(this._longPressTimer);
    this._longPressPos = this._getPos(e, canvas);
    this._longPressPage = pageNum;
    this._longPressTimer = setTimeout(() => {
      this._handleLongPress(pageNum, this._longPressPos);
    }, 600);
  }

  _onPointerMove(e, pageNum, canvas) {
    clearTimeout(this._longPressTimer); // Cancel long press if finger moves
    if (this.activeTool === 'draw' && this._isDrawing) {
      const pos = this._getPos(e, canvas);
      this._currentPath.push(pos);
      this._drawLiveStroke(canvas);
    }
  }

  _onPointerUp(e, pageNum, canvas, notesDiv) {
    if (this.activeTool === 'draw' && this._isDrawing) {
      this._isDrawing = false;
      if (this._currentPath.length > 1) {
        if (!this._drawPaths[pageNum]) this._drawPaths[pageNum] = [];
        const pathCopy = [...this._currentPath];
        this._drawPaths[pageNum].push(pathCopy);
        this._pushUndo({ type: 'draw', pageNum, path: pathCopy });
      }
      this._currentPath = [];
    } else if (this.activeTool === 'text') {
      clearTimeout(this._longPressTimer);
      const pos = this._getPos(e, canvas);
      this._insertText(pageNum, pos);
    } else if (this.activeTool === 'note') {
      const pos = this._getPos(e, canvas);
      this._addNote(pageNum, pos, notesDiv);
    } else if (this.activeTool === 'redact' && this._isDrawing) {
      this._isDrawing = false;
      const pos = this._getPos(e, canvas);
      if (this._redactStart) {
        this._addRedaction(pageNum, this._redactStart, pos, canvas);
        this._redactStart = null;
      }
    } else if (this.activeTool === 'highlight') {
      const selection = window.getSelection();
      if (selection && selection.toString().trim()) {
        this._highlightSelection(pageNum, canvas);
      }
    }
  }

  _drawLiveStroke(canvas) {
    const ctx = canvas.getContext('2d');
    // Don't clear — keep existing annotations
    this._redrawAnnotations(this.currentPage, canvas);

    ctx.save();
    ctx.strokeStyle = '#e94560';
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.beginPath();
    this._currentPath.forEach((p, i) => {
      if (i === 0) ctx.moveTo(p.x, p.y);
      else ctx.lineTo(p.x, p.y);
    });
    ctx.stroke();
    ctx.restore();
  }

  _redrawAnnotations(pageNum, canvas) {
    const ctx = canvas.getContext('2d');
    const w = canvas.width / 2;
    const h = canvas.height / 2;
    ctx.clearRect(0, 0, w, h);

    // Draw paths
    const paths = this._drawPaths[pageNum] || [];
    paths.forEach(path => {
      ctx.save();
      ctx.strokeStyle = '#e94560';
      ctx.lineWidth = 2;
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
      ctx.beginPath();
      path.forEach((p, i) => {
        if (i === 0) ctx.moveTo(p.x, p.y);
        else ctx.lineTo(p.x, p.y);
      });
      ctx.stroke();
      ctx.restore();
    });

    // Highlights
    const highlights = this._highlights[pageNum] || [];
    highlights.forEach(hl => {
      ctx.save();
      ctx.fillStyle = 'rgba(255, 235, 59, 0.35)';
      hl.rects.forEach(r => ctx.fillRect(r.x, r.y, r.w, r.h));
      ctx.restore();
    });

    // Redactions
    const redactions = this._redactions[pageNum] || [];
    redactions.forEach(r => {
      ctx.save();
      ctx.fillStyle = '#000000';
      ctx.fillRect(r.x, r.y, r.w, r.h);
      ctx.restore();
    });

    // Stamp annotations (Approved, Draft, etc.)
    const stampAnnots = (this.annotations[pageNum] || []).filter(a => a.type === 'stamp');
    stampAnnots.forEach(s => {
      ctx.save();
      ctx.strokeStyle = s.color;
      ctx.lineWidth = 3;
      ctx.fillStyle = s.color;
      ctx.font = 'bold 28px sans-serif';
      ctx.globalAlpha = 0.6;
      ctx.strokeRect(s.x, s.y, s.w, s.h);
      ctx.fillText(s.label, s.x + 12, s.y + 29);
      ctx.restore();
    });

    // Signature stamps
    const sigStamps = (this.annotations[pageNum] || []).filter(a => a.type === 'signature');
    sigStamps.forEach(stamp => {
      const img = new Image();
      img.src = stamp.dataUrl;
      img.onload = () => {
        ctx.drawImage(img, stamp.x, stamp.y, stamp.width, stamp.height);
      };
    });
  }

  // ==========================================
  // ANNOTATIONS
  // ==========================================

  _toggleTool(tool) {
    if (this.activeTool === tool) {
      this.activeTool = null;
    } else {
      this.activeTool = tool;
    }
    // Exit edit text mode when activating an annotation tool
    if (this.activeTool && this._editTextMode) {
      this._editTextMode = false;
      const editBtn = document.getElementById('pdf-edit-text');
      if (editBtn) editBtn.classList.remove('active');
      document.querySelectorAll('.pdf-text-layer').forEach(tl => tl.classList.remove('pdf-edit-mode'));
    }
    // Update button active states
    ['highlight', 'note', 'draw', 'text', 'redact'].forEach(t => {
      const btn = document.getElementById(`pdf-${t}`);
      if (btn) btn.classList.toggle('active', this.activeTool === t);
    });
    // Change cursor and pointer-events on overlays
    const viewer = document.getElementById('pdf-viewer');
    if (viewer) {
      viewer.style.cursor = this.activeTool === 'draw' ? 'crosshair' :
                             this.activeTool === 'note' ? 'cell' :
                             this.activeTool === 'text' ? 'text' :
                             this.activeTool === 'sign' ? 'copy' :
                             this.activeTool === 'redact' ? 'crosshair' : '';
    }
    // When no tool active, let touches pass through overlay for scrolling
    document.querySelectorAll('.pdf-annotation-layer').forEach(layer => {
      layer.style.pointerEvents = this.activeTool ? 'auto' : 'none';
    });
  }

  _highlightSelection(pageNum, canvas) {
    const selection = window.getSelection();
    if (!selection || selection.rangeCount === 0) return;

    const range = selection.getRangeAt(0);
    const rects = range.getClientRects();
    const canvasRect = canvas.getBoundingClientRect();

    const hlRects = [];
    for (let i = 0; i < rects.length; i++) {
      hlRects.push({
        x: rects[i].left - canvasRect.left,
        y: rects[i].top - canvasRect.top,
        w: rects[i].width,
        h: rects[i].height
      });
    }

    if (hlRects.length > 0) {
      if (!this._highlights[pageNum]) this._highlights[pageNum] = [];
      const highlight = { rects: hlRects };
      this._highlights[pageNum].push(highlight);
      this._pushUndo({ type: 'highlight', pageNum, highlight });
      this._redrawAnnotations(pageNum, canvas);
    }
    selection.removeAllRanges();
  }

  // ==========================================
  // TEXT ANNOTATIONS (click to insert, hold to edit)
  // ==========================================

  _insertText(pageNum, pos) {
    const pageDiv = document.querySelector(`.pdf-page[data-page="${pageNum}"]`);
    if (!pageDiv) return;

    if (!this._textAnnotations[pageNum]) this._textAnnotations[pageNum] = [];
    const annotation = { x: pos.x, y: pos.y, text: '', fontSize: 14, color: '#000000', width: 200 };
    this._textAnnotations[pageNum].push(annotation);
    const idx = this._textAnnotations[pageNum].length - 1;
    this._pushUndo({ type: 'textAnnotation', pageNum, annotation, idx });
    this._createTextElement(pageNum, annotation, idx, pageDiv);
  }

  _createTextElement(pageNum, annotation, idx, pageDiv) {
    const el = document.createElement('div');
    el.className = 'pdf-text-annotation';
    el.dataset.page = pageNum;
    el.dataset.idx = idx;
    el.style.left = annotation.x + 'px';
    el.style.top = annotation.y + 'px';
    el.style.width = annotation.width + 'px';
    el.style.fontSize = annotation.fontSize + 'px';
    el.style.color = annotation.color;

    const input = document.createElement('textarea');
    input.className = 'pdf-text-input';
    input.value = annotation.text;
    input.placeholder = 'Type here...';
    input.style.fontSize = annotation.fontSize + 'px';
    input.style.color = annotation.color;
    input.style.width = annotation.width + 'px';

    input.addEventListener('input', () => {
      annotation.text = input.value;
      // Auto-resize height
      input.style.height = 'auto';
      input.style.height = input.scrollHeight + 'px';
    });
    input.addEventListener('blur', () => {
      if (!annotation.text.trim()) {
        // Remove empty annotations
        this._textAnnotations[pageNum].splice(idx, 1);
        el.remove();
        return;
      }
      // Switch to display mode
      el.classList.add('committed');
      input.readOnly = true;
    });

    const deleteBtn = document.createElement('button');
    deleteBtn.className = 'pdf-text-delete';
    deleteBtn.textContent = '\u2715';
    deleteBtn.addEventListener('click', (e) => {
      e.stopPropagation();
      this._textAnnotations[pageNum].splice(idx, 1);
      el.remove();
    });

    // Make draggable
    let dragStartX, dragStartY, origX, origY;
    const header = document.createElement('div');
    header.className = 'pdf-text-drag-handle';
    header.innerHTML = '\u2630';
    header.addEventListener('mousedown', (e) => this._startDrag(e, el, annotation));
    header.addEventListener('touchstart', (e) => {
      e.preventDefault();
      this._startDrag(e.touches[0], el, annotation);
    }, { passive: false });

    el.appendChild(header);
    el.appendChild(deleteBtn);
    el.appendChild(input);

    // Find the notes layer (same z-level)
    const notesLayer = pageDiv.querySelector('.pdf-notes-layer');
    if (notesLayer) notesLayer.appendChild(el);
    else pageDiv.appendChild(el);

    // Focus the input for immediate typing
    setTimeout(() => { input.focus(); input.style.height = input.scrollHeight + 'px'; }, 50);
  }

  _startDrag(e, el, annotation) {
    const startX = e.clientX;
    const startY = e.clientY;
    const origLeft = parseInt(el.style.left);
    const origTop = parseInt(el.style.top);

    const onMove = (ev) => {
      const cx = ev.touches ? ev.touches[0].clientX : ev.clientX;
      const cy = ev.touches ? ev.touches[0].clientY : ev.clientY;
      const dx = cx - startX;
      const dy = cy - startY;
      el.style.left = (origLeft + dx) + 'px';
      el.style.top = (origTop + dy) + 'px';
      annotation.x = origLeft + dx;
      annotation.y = origTop + dy;
    };
    const onUp = () => {
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
      document.removeEventListener('touchmove', onMove);
      document.removeEventListener('touchend', onUp);
    };
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
    document.addEventListener('touchmove', onMove, { passive: false });
    document.addEventListener('touchend', onUp);
  }

  _handleLongPress(pageNum, pos) {
    // Find if there's a text annotation near this position
    const annotations = this._textAnnotations[pageNum] || [];
    for (let i = 0; i < annotations.length; i++) {
      const a = annotations[i];
      if (Math.abs(pos.x - a.x) < a.width && pos.y >= a.y && pos.y <= a.y + 60) {
        // Found a text annotation — make it editable
        const el = document.querySelector(`.pdf-text-annotation[data-page="${pageNum}"][data-idx="${i}"]`);
        if (el) {
          el.classList.remove('committed');
          const input = el.querySelector('.pdf-text-input');
          if (input) { input.readOnly = false; input.focus(); }
        }
        return;
      }
    }
  }

  _renderTextAnnotations(pageNum, pageDiv) {
    const annotations = this._textAnnotations[pageNum] || [];
    annotations.forEach((annotation, idx) => {
      if (annotation.text.trim()) {
        this._createTextElement(pageNum, annotation, idx, pageDiv);
        // Mark as committed since it has text
        setTimeout(() => {
          const el = document.querySelector(`.pdf-text-annotation[data-page="${pageNum}"][data-idx="${idx}"]`);
          if (el) {
            el.classList.add('committed');
            const input = el.querySelector('.pdf-text-input');
            if (input) input.readOnly = true;
          }
        }, 60);
      }
    });
  }

  // ==========================================
  // EDIT TEXT MODE — toggle visible text, single-tap to edit
  // ==========================================

  _toggleEditTextMode() {
    this._editTextMode = !this._editTextMode;
    const btn = document.getElementById('pdf-edit-text');
    if (btn) btn.classList.toggle('active', this._editTextMode);

    // Toggle edit mode class on all text layers
    document.querySelectorAll('.pdf-text-layer').forEach(tl => {
      tl.classList.toggle('pdf-edit-mode', this._editTextMode);
    });

    // When edit mode active, annotation overlay must not intercept taps
    document.querySelectorAll('.pdf-annotation-layer').forEach(ol => {
      ol.style.pointerEvents = this._editTextMode ? 'none' : (this.activeTool ? 'auto' : 'none');
    });

    // Deactivate annotation tools when entering edit mode
    if (this._editTextMode && this.activeTool) {
      this._toggleTool(this.activeTool);
    }
  }

  // ==========================================
  // EDITABLE TEXT LAYER (edit mode = tap, fallback = long-press)
  // ==========================================

  _wireTextEditing(textDiv, pageNum) {
    let pressTimer = null;
    let pressSpan = null;
    let pressStartPos = null;

    const startPress = (span, clientX, clientY) => {
      clearTimeout(pressTimer);
      pressSpan = span;
      pressStartPos = { x: clientX, y: clientY };
      span.classList.add('pdf-text-pressing');
      pressTimer = setTimeout(() => {
        span.classList.remove('pdf-text-pressing');
        // Long-press: show context menu if edited, otherwise start editing
        if (span.classList.contains('pdf-text-edited')) {
          this._showTextContextMenu(span, pageNum, clientX, clientY);
        } else {
          this._makeSpanEditable(span, pageNum);
        }
      }, 500);
    };

    const cancelPress = () => {
      clearTimeout(pressTimer);
      if (pressSpan) pressSpan.classList.remove('pdf-text-pressing');
      pressSpan = null;
    };

    // Mouse: long-press fallback when NOT in edit mode
    textDiv.addEventListener('mousedown', (e) => {
      const span = e.target.closest('span[data-item-idx]');
      if (!span || this.activeTool) return;
      if (!this._editTextMode) startPress(span, e.clientX, e.clientY);
    });
    textDiv.addEventListener('mouseup', cancelPress);
    textDiv.addEventListener('mouseleave', cancelPress);
    textDiv.addEventListener('mousemove', (e) => {
      if (pressSpan && pressStartPos) {
        const dx = Math.abs(e.clientX - pressStartPos.x);
        const dy = Math.abs(e.clientY - pressStartPos.y);
        if (dx > 5 || dy > 5) cancelPress();
      }
    });

    // Click: single tap in edit mode
    textDiv.addEventListener('click', (e) => {
      if (!this._editTextMode) return;
      const span = e.target.closest('span[data-item-idx]');
      if (span) {
        e.stopPropagation();
        this._makeSpanEditable(span, pageNum);
      }
    });

    // Touch: long-press OR single tap
    textDiv.addEventListener('touchstart', (e) => {
      const span = e.target.closest('span[data-item-idx]');
      if (!span || this.activeTool) return;
      const touch = e.touches[0];
      if (this._editTextMode) {
        // In edit mode: long-press for context menu on edited spans
        if (span.classList.contains('pdf-text-edited')) {
          startPress(span, touch.clientX, touch.clientY);
        }
        // Single tap handled by touchend
      } else {
        startPress(span, touch.clientX, touch.clientY);
      }
    }, { passive: true });

    textDiv.addEventListener('touchend', (e) => {
      cancelPress();
      if (this._editTextMode) {
        const touch = e.changedTouches[0];
        const el = document.elementFromPoint(touch.clientX, touch.clientY);
        const span = el?.closest('span[data-item-idx]');
        if (span && textDiv.contains(span)) {
          e.preventDefault();
          this._makeSpanEditable(span, pageNum);
        }
      }
    }, { passive: false });

    textDiv.addEventListener('touchmove', cancelPress, { passive: true });
  }

  _makeSpanEditable(span, pageNum) {
    if (span.contentEditable === 'true') return; // Already editing

    const itemIdx = parseInt(span.dataset.itemIdx);
    const entry = this._pageEntries[pageNum]?.[itemIdx];
    if (!entry) return;

    const originalText = entry.item.str;
    const textBeforeEdit = span.textContent;

    // Record original rendered width so white bg always covers canvas text
    const origWidth = span.offsetWidth;

    // Make editable
    span.contentEditable = 'true';
    span.classList.add('pdf-text-editing');
    span.classList.remove('pdf-text-edited');
    span.style.minWidth = origWidth + 'px';
    span.focus();

    // Select all text for easy replacement
    const sel = window.getSelection();
    const range = document.createRange();
    range.selectNodeContents(span);
    sel.removeAllRanges();
    sel.addRange(range);

    const commit = () => {
      span.contentEditable = 'false';
      span.classList.remove('pdf-text-editing');

      const newText = span.textContent;
      if (newText !== originalText) {
        // Push to undo stack
        this._pushUndo({ type: 'textEdit', pageNum, itemIdx, before: textBeforeEdit, after: newText });

        if (!this._textEdits[pageNum]) this._textEdits[pageNum] = {};
        this._textEdits[pageNum][itemIdx] = {
          original: originalText,
          edited: newText,
          entry
        };
        span.classList.add('pdf-text-edited');
        span.style.minWidth = origWidth + 'px';
      } else {
        delete this._textEdits[pageNum]?.[itemIdx];
        span.classList.remove('pdf-text-edited');
        span.style.minWidth = '';
      }
      span.removeEventListener('blur', commit);
      span.removeEventListener('keydown', onKey);
    };

    const onKey = (e) => {
      if (e.key === 'Enter') { e.preventDefault(); commit(); }
      if (e.key === 'Escape') { span.textContent = textBeforeEdit; commit(); }
    };

    span.addEventListener('blur', commit);
    span.addEventListener('keydown', onKey);
  }

  // ==========================================
  // LONG-PRESS CONTEXT MENU (undo, re-edit, revert)
  // ==========================================

  _showTextContextMenu(span, pageNum, clientX, clientY) {
    // Remove any existing context menu
    this._dismissContextMenu();

    const itemIdx = parseInt(span.dataset.itemIdx);
    const entry = this._pageEntries[pageNum]?.[itemIdx];
    if (!entry) return;

    const menu = document.createElement('div');
    menu.className = 'pdf-context-menu';
    this._activeContextMenu = menu;

    // Undo — revert to original
    const undoBtn = document.createElement('button');
    undoBtn.textContent = '\u21A9 Undo Edit';
    undoBtn.addEventListener('click', () => {
      const originalText = entry.item.str;
      const currentText = span.textContent;
      this._pushUndo({ type: 'textEdit', pageNum, itemIdx, before: currentText, after: originalText });
      span.textContent = originalText;
      span.classList.remove('pdf-text-edited');
      span.style.minWidth = '';
      delete this._textEdits[pageNum]?.[itemIdx];
      this._dismissContextMenu();
    });
    menu.appendChild(undoBtn);

    // Re-edit
    const editBtn = document.createElement('button');
    editBtn.textContent = '\u270E Edit Again';
    editBtn.addEventListener('click', () => {
      this._dismissContextMenu();
      this._makeSpanEditable(span, pageNum);
    });
    menu.appendChild(editBtn);

    // Position menu near the touch point
    const pageDiv = span.closest('.pdf-page');
    if (pageDiv) {
      const rect = pageDiv.getBoundingClientRect();
      menu.style.left = (clientX - rect.left) + 'px';
      menu.style.top = (clientY - rect.top + 8) + 'px';
      pageDiv.appendChild(menu);
    }

    // Dismiss on tap elsewhere
    const dismiss = (e) => {
      if (!menu.contains(e.target)) {
        this._dismissContextMenu();
        document.removeEventListener('click', dismiss, true);
        document.removeEventListener('touchstart', dismiss, true);
      }
    };
    setTimeout(() => {
      document.addEventListener('click', dismiss, true);
      document.addEventListener('touchstart', dismiss, true);
    }, 50);
  }

  _dismissContextMenu() {
    if (this._activeContextMenu) {
      this._activeContextMenu.remove();
      this._activeContextMenu = null;
    }
  }

  // ==========================================
  // UNDO / REDO
  // ==========================================

  _pushUndo(action) {
    this._undoStack.push(action);
    this._redoStack = [];
    if (this._undoStack.length > 50) this._undoStack.shift();
  }

  _undo() {
    if (this._undoStack.length === 0) return;
    const action = this._undoStack.pop();
    this._redoStack.push(action);
    this._applyUndoAction(action, true);
  }

  _redo() {
    if (this._redoStack.length === 0) return;
    const action = this._redoStack.pop();
    this._undoStack.push(action);
    this._applyUndoAction(action, false);
  }

  _applyUndoAction(action, isUndo) {
    if (action.type === 'textEdit') {
      const { pageNum, itemIdx, before, after } = action;
      const text = isUndo ? before : after;
      const entry = this._pageEntries[pageNum]?.[itemIdx];
      const originalText = entry?.item?.str || '';

      if (text === originalText) {
        delete this._textEdits[pageNum]?.[itemIdx];
      } else {
        if (!this._textEdits[pageNum]) this._textEdits[pageNum] = {};
        this._textEdits[pageNum][itemIdx] = {
          original: originalText,
          edited: text,
          entry
        };
      }

      const span = document.querySelector(`.pdf-text-layer span[data-page="${pageNum}"][data-item-idx="${itemIdx}"]`);
      if (span) {
        span.textContent = text;
        if (text === originalText) {
          span.classList.remove('pdf-text-edited');
          span.style.minWidth = '';
        } else {
          span.classList.add('pdf-text-edited');
        }
      }
    } else if (action.type === 'redaction') {
      const { pageNum, redaction } = action;
      if (isUndo) {
        const arr = this._redactions[pageNum] || [];
        const idx = arr.findIndex(r => r.x === redaction.x && r.y === redaction.y && r.w === redaction.w);
        if (idx >= 0) arr.splice(idx, 1);
      } else {
        if (!this._redactions[pageNum]) this._redactions[pageNum] = [];
        this._redactions[pageNum].push(redaction);
      }
      const overlay = document.querySelector(`.pdf-page[data-page="${pageNum}"] .pdf-annotation-layer`);
      if (overlay) this._redrawAnnotations(pageNum, overlay);

    } else if (action.type === 'draw') {
      const { pageNum, path } = action;
      if (isUndo) {
        const arr = this._drawPaths[pageNum] || [];
        const idx = arr.indexOf(path);
        if (idx >= 0) arr.splice(idx, 1);
      } else {
        if (!this._drawPaths[pageNum]) this._drawPaths[pageNum] = [];
        this._drawPaths[pageNum].push(path);
      }
      this._redrawOverlay(pageNum);

    } else if (action.type === 'highlight') {
      const { pageNum, highlight } = action;
      if (isUndo) {
        const arr = this._highlights[pageNum] || [];
        const idx = arr.indexOf(highlight);
        if (idx >= 0) arr.splice(idx, 1);
      } else {
        if (!this._highlights[pageNum]) this._highlights[pageNum] = [];
        this._highlights[pageNum].push(highlight);
      }
      this._redrawOverlay(pageNum);

    } else if (action.type === 'note') {
      const { pageNum, note } = action;
      if (isUndo) {
        const arr = this._notes[pageNum] || [];
        const idx = arr.indexOf(note);
        if (idx >= 0) arr.splice(idx, 1);
      } else {
        if (!this._notes[pageNum]) this._notes[pageNum] = [];
        this._notes[pageNum].push(note);
      }
      const notesDiv = document.querySelector(`.pdf-page[data-page="${pageNum}"] .pdf-notes-layer`);
      if (notesDiv) this._redrawNotes(pageNum, notesDiv);

    } else if (action.type === 'textAnnotation') {
      const { pageNum, annotation, idx } = action;
      if (isUndo) {
        const arr = this._textAnnotations[pageNum] || [];
        const aIdx = arr.indexOf(annotation);
        if (aIdx >= 0) arr.splice(aIdx, 1);
        // Remove the DOM element
        const el = document.querySelector(`.pdf-text-annotation[data-page="${pageNum}"][data-idx="${idx}"]`);
        if (el) el.remove();
      } else {
        if (!this._textAnnotations[pageNum]) this._textAnnotations[pageNum] = [];
        this._textAnnotations[pageNum].push(annotation);
        const pageDiv = document.querySelector(`.pdf-page[data-page="${pageNum}"]`);
        if (pageDiv) {
          const newIdx = this._textAnnotations[pageNum].length - 1;
          this._createTextElement(pageNum, annotation, newIdx, pageDiv);
        }
      }

    } else if (action.type === 'stamp' || action.type === 'signature') {
      const { pageNum, annotation: ann } = action;
      if (isUndo) {
        const arr = this.annotations[pageNum] || [];
        const idx = arr.indexOf(ann);
        if (idx >= 0) arr.splice(idx, 1);
      } else {
        if (!this.annotations[pageNum]) this.annotations[pageNum] = [];
        this.annotations[pageNum].push(ann);
      }
      this._redrawOverlay(pageNum);
    }
  }

  _redrawOverlay(pageNum) {
    const overlay = document.querySelector(`.pdf-page[data-page="${pageNum}"] .pdf-annotation-layer`);
    if (overlay) this._redrawAnnotations(pageNum, overlay);
  }

  _addNote(pageNum, pos, notesDiv) {
    const text = prompt('Add note:');
    if (!text) return;

    if (!this._notes[pageNum]) this._notes[pageNum] = [];
    const note = { x: pos.x, y: pos.y, text };
    this._notes[pageNum].push(note);
    this._pushUndo({ type: 'note', pageNum, note });
    this._redrawNotes(pageNum, notesDiv);
  }

  _redrawNotes(pageNum, notesDiv) {
    if (!notesDiv) return;
    notesDiv.innerHTML = '';
    const notes = this._notes[pageNum] || [];
    notes.forEach((note, i) => {
      const el = document.createElement('div');
      el.className = 'pdf-sticky-note';
      el.style.left = note.x + 'px';
      el.style.top = note.y + 'px';
      el.innerHTML = `
        <div class="pdf-note-icon" title="${note.text.replace(/"/g, '&quot;')}">📝</div>
        <div class="pdf-note-body">${note.text}</div>
        <button class="pdf-note-delete" data-idx="${i}">&times;</button>
      `;
      el.querySelector('.pdf-note-delete').addEventListener('click', () => {
        this._notes[pageNum].splice(i, 1);
        this._redrawNotes(pageNum, notesDiv);
      });
      // Toggle body on click
      el.querySelector('.pdf-note-icon').addEventListener('click', () => {
        el.classList.toggle('expanded');
      });
      notesDiv.appendChild(el);
    });
  }

  // ==========================================
  // SIGNATURE PAD
  // ==========================================

  _showSignaturePad() {
    const existing = document.getElementById('signature-modal');
    if (existing) existing.remove();

    const modal = document.createElement('div');
    modal.id = 'signature-modal';
    modal.style.cssText = `
      position:fixed;inset:0;z-index:500;background:rgba(0,0,0,0.5);
      display:flex;align-items:center;justify-content:center;
      backdrop-filter:blur(4px);-webkit-backdrop-filter:blur(4px);
    `;

    const panel = document.createElement('div');
    panel.style.cssText = `
      background:white;border-radius:12px;padding:24px;width:500px;max-width:90vw;
      box-shadow:0 16px 48px rgba(0,0,0,0.3);
    `;

    panel.innerHTML = `
      <div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:12px">
        <strong style="font-size:16px">Draw Your Signature</strong>
        <button id="sig-close" style="background:none;border:none;font-size:20px;cursor:pointer;color:#999">\u2715</button>
      </div>
      <div style="margin-bottom:8px;font-size:12px;color:#888">Draw with your mouse or finger below</div>
      <canvas id="sig-canvas" width="900" height="300" style="width:100%;height:150px;border:2px solid #e5e7eb;border-radius:8px;cursor:crosshair;touch-action:none;background:white"></canvas>
      <div style="display:flex;gap:8px;margin-top:12px">
        <button id="sig-clear" style="flex:1;padding:10px;border:1px solid #ddd;border-radius:6px;background:white;cursor:pointer;font-size:13px">Clear</button>
        <button id="sig-save" style="flex:1;padding:10px;border:none;border-radius:6px;background:linear-gradient(145deg,#1e40af,#1e3a8a);color:white;cursor:pointer;font-size:13px;font-weight:600">Use Signature</button>
      </div>
      <div id="sig-saved-list" style="margin-top:12px"></div>
    `;

    modal.appendChild(panel);
    modal.addEventListener('click', (e) => { if (e.target === modal) modal.remove(); });
    document.body.appendChild(modal);

    const canvas = document.getElementById('sig-canvas');
    const ctx = canvas.getContext('2d');
    ctx.lineWidth = 3;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.strokeStyle = '#1a1a1a';
    let drawing = false;
    let lastX = 0, lastY = 0;

    const getXY = (e) => {
      const rect = canvas.getBoundingClientRect();
      const scaleX = canvas.width / rect.width;
      const scaleY = canvas.height / rect.height;
      const clientX = e.touches ? e.touches[0].clientX : e.clientX;
      const clientY = e.touches ? e.touches[0].clientY : e.clientY;
      return { x: (clientX - rect.left) * scaleX, y: (clientY - rect.top) * scaleY };
    };

    canvas.addEventListener('mousedown', (e) => { drawing = true; const p = getXY(e); lastX = p.x; lastY = p.y; });
    canvas.addEventListener('mousemove', (e) => {
      if (!drawing) return;
      const p = getXY(e);
      ctx.beginPath(); ctx.moveTo(lastX, lastY); ctx.lineTo(p.x, p.y); ctx.stroke();
      lastX = p.x; lastY = p.y;
    });
    canvas.addEventListener('mouseup', () => drawing = false);
    canvas.addEventListener('mouseleave', () => drawing = false);
    canvas.addEventListener('touchstart', (e) => { e.preventDefault(); drawing = true; const p = getXY(e); lastX = p.x; lastY = p.y; }, { passive: false });
    canvas.addEventListener('touchmove', (e) => {
      e.preventDefault();
      if (!drawing) return;
      const p = getXY(e);
      ctx.beginPath(); ctx.moveTo(lastX, lastY); ctx.lineTo(p.x, p.y); ctx.stroke();
      lastX = p.x; lastY = p.y;
    }, { passive: false });
    canvas.addEventListener('touchend', () => drawing = false);

    document.getElementById('sig-clear').addEventListener('click', () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
    });

    document.getElementById('sig-close').addEventListener('click', () => modal.remove());

    document.getElementById('sig-save').addEventListener('click', () => {
      const dataUrl = canvas.toDataURL('image/png');
      this.signatures.push(dataUrl);
      this._stampQueue = dataUrl;
      this.activeTool = 'sign';
      const viewer = document.getElementById('pdf-viewer');
      if (viewer) viewer.style.cursor = 'copy';
      modal.remove();
    });

    // Show saved signatures
    this._renderSavedSignatures();
  }

  _renderSavedSignatures() {
    const list = document.getElementById('sig-saved-list');
    if (!list || this.signatures.length === 0) return;
    list.innerHTML = '<div style="font-size:12px;color:#888;margin-bottom:4px">Saved signatures:</div>';
    this.signatures.forEach((sig, i) => {
      const img = document.createElement('img');
      img.src = sig;
      img.style.cssText = 'height:40px;border:1px solid #ddd;border-radius:4px;margin-right:8px;cursor:pointer;background:white;padding:4px;';
      img.title = 'Click to use this signature';
      img.addEventListener('click', () => {
        this._stampQueue = sig;
        this.activeTool = 'sign';
        const viewer = document.getElementById('pdf-viewer');
        if (viewer) viewer.style.cursor = 'copy';
        document.getElementById('signature-modal')?.remove();
      });
      list.appendChild(img);
    });
  }

  _stampSignature(pageNum, pos, canvas) {
    if (!this._stampQueue) return;
    const width = 150;
    const height = 60;

    if (!this.annotations[pageNum]) this.annotations[pageNum] = [];
    const sigAnnotation = {
      type: 'signature',
      dataUrl: this._stampQueue,
      x: pos.x - width / 2,
      y: pos.y - height / 2,
      width,
      height
    };
    this.annotations[pageNum].push(sigAnnotation);
    this._pushUndo({ type: 'signature', pageNum, annotation: sigAnnotation });

    this._redrawAnnotations(pageNum, canvas);
    this._stampQueue = null;
    this.activeTool = null;
    const viewer = document.getElementById('pdf-viewer');
    if (viewer) viewer.style.cursor = '';
  }

  // ==========================================
  // THUMBNAILS
  // ==========================================

  async _generateThumbnails() {
    const sidebar = document.getElementById('pdf-thumbnails');
    if (!sidebar || !this.pdfDoc) return;
    sidebar.innerHTML = '';

    for (let i = 1; i <= this.totalPages; i++) {
      const page = await this.pdfDoc.getPage(i);
      const scale = 0.3;
      const rotation = this.rotations[i] || 0;
      const vp = page.getViewport({ scale, rotation });

      const thumb = document.createElement('div');
      thumb.className = 'pdf-thumbnail' + (i === this.currentPage ? ' active' : '');
      thumb.dataset.page = i;

      const canvas = document.createElement('canvas');
      canvas.width = vp.width * 2;
      canvas.height = vp.height * 2;
      canvas.style.width = vp.width + 'px';
      canvas.style.height = vp.height + 'px';
      const ctx = canvas.getContext('2d');
      ctx.scale(2, 2);
      await page.render({ canvasContext: ctx, viewport: vp }).promise;

      const label = document.createElement('span');
      label.className = 'pdf-thumb-label';
      label.textContent = i;

      thumb.appendChild(canvas);
      thumb.appendChild(label);
      thumb.addEventListener('click', () => this._goToPage(i));
      sidebar.appendChild(thumb);
    }
  }

  _highlightThumbnail(pageNum) {
    document.querySelectorAll('.pdf-thumbnail').forEach(t => {
      t.classList.toggle('active', parseInt(t.dataset.page) === pageNum);
    });
  }

  // ==========================================
  // NAVIGATION
  // ==========================================

  _goToPage(num) {
    if (num < 1 || num > this.totalPages) return;
    this.currentPage = num;
    this._updatePageInfo();

    const pageEl = document.querySelector(`.pdf-page[data-page="${num}"]`);
    if (pageEl) pageEl.scrollIntoView({ behavior: 'smooth', block: 'start' });
    this._highlightThumbnail(num);
  }

  _updatePageInfo() {
    const input = document.getElementById('pdf-page-input');
    const total = document.getElementById('pdf-page-total');
    if (input) input.value = this.currentPage;
    if (total) total.textContent = `of ${this.totalPages}`;
  }

  // ==========================================
  // ZOOM
  // ==========================================

  _setZoom(level) {
    this.scale = Math.round(level * 100) / 100;
    const zoomSel = document.getElementById('pdf-zoom-select');
    if (zoomSel) {
      const match = Array.from(zoomSel.options).find(o => parseFloat(o.value) === this.scale);
      if (match) zoomSel.value = match.value;
    }
    this._renderAllPages();
    this._generateThumbnails();
  }

  _fitWidth() {
    const container = document.getElementById('pdf-viewer-container');
    if (!container || !this.pdfDoc) return;
    this.pdfDoc.getPage(1).then(page => {
      const vp = page.getViewport({ scale: 1 });
      const containerWidth = container.clientWidth - 40;
      this.scale = containerWidth / vp.width;
      this._renderAllPages();
      this._generateThumbnails();
    });
  }

  _fitPage() {
    const container = document.getElementById('pdf-viewer-container');
    if (!container || !this.pdfDoc) return;
    this.pdfDoc.getPage(1).then(page => {
      const vp = page.getViewport({ scale: 1 });
      const cw = container.clientWidth - 40;
      const ch = container.clientHeight - 40;
      this.scale = Math.min(cw / vp.width, ch / vp.height);
      this._renderAllPages();
      this._generateThumbnails();
    });
  }

  // ==========================================
  // SEARCH
  // ==========================================

  _searchDialog() {
    const existing = document.getElementById('pdf-search-bar');
    if (existing) { existing.remove(); this._clearSearchHighlights(); return; }

    const bar = document.createElement('div');
    bar.id = 'pdf-search-bar';
    bar.style.cssText = `
      position:absolute;top:0;right:0;z-index:50;background:white;
      padding:8px 12px;border-radius:0 0 0 8px;box-shadow:0 2px 12px rgba(0,0,0,0.15);
      display:flex;gap:6px;align-items:center;
    `;
    bar.innerHTML = `
      <input id="pdf-search-input" type="text" placeholder="Search..." style="padding:6px 10px;border:1px solid #ddd;border-radius:4px;font-size:13px;width:160px">
      <span id="pdf-search-count" style="font-size:11px;color:#666;min-width:50px;text-align:center"></span>
      <button id="pdf-search-prev" style="padding:4px 8px;background:#f3f4f6;border:1px solid #ddd;border-radius:4px;cursor:pointer;font-size:14px" title="Previous match">&#9650;</button>
      <button id="pdf-search-next" style="padding:4px 8px;background:#f3f4f6;border:1px solid #ddd;border-radius:4px;cursor:pointer;font-size:14px" title="Next match">&#9660;</button>
      <button id="pdf-search-close" style="background:none;border:none;font-size:16px;cursor:pointer;color:#999">\u2715</button>
    `;

    const viewer = document.getElementById('pdf-viewer-container');
    if (viewer) {
      viewer.style.position = 'relative';
      viewer.appendChild(bar);
    }

    document.getElementById('pdf-search-close').addEventListener('click', () => { bar.remove(); this._clearSearchHighlights(); });
    document.getElementById('pdf-search-next').addEventListener('click', () => this._searchNav(1));
    document.getElementById('pdf-search-prev').addEventListener('click', () => this._searchNav(-1));

    const input = document.getElementById('pdf-search-input');
    let debounce = null;
    input.addEventListener('input', () => {
      clearTimeout(debounce);
      debounce = setTimeout(() => this._doSearch(), 200);
    });
    input.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') { e.preventDefault(); this._searchNav(e.shiftKey ? -1 : 1); }
      if (e.key === 'Escape') { bar.remove(); this._clearSearchHighlights(); }
    });
    input.focus();
  }

  _doSearch() {
    const input = document.getElementById('pdf-search-input');
    if (!input || !this.pdfDoc) return;
    const query = input.value.trim().toLowerCase();

    this._clearSearchHighlights();

    if (!query) {
      const countEl = document.getElementById('pdf-search-count');
      if (countEl) countEl.textContent = '';
      return;
    }

    // Find matches in rendered text layer spans
    document.querySelectorAll('.pdf-text-layer span[data-item-idx]').forEach(span => {
      const text = span.textContent.toLowerCase();
      if (text.includes(query)) {
        this._searchMatches.push(span);
        span.classList.add('pdf-search-match');
      }
    });

    const countEl = document.getElementById('pdf-search-count');
    if (countEl) {
      countEl.textContent = this._searchMatches.length > 0
        ? `${this._searchMatches.length} found`
        : 'No matches';
    }

    if (this._searchMatches.length > 0) {
      this._searchIdx = 0;
      this._highlightCurrentMatch();
    }
  }

  _searchNav(dir) {
    if (this._searchMatches.length === 0) {
      // If no matches yet, run the search first
      this._doSearch();
      if (this._searchMatches.length === 0) return;
    }
    if (this._searchIdx >= 0 && this._searchIdx < this._searchMatches.length) {
      this._searchMatches[this._searchIdx].classList.remove('pdf-search-current');
    }
    this._searchIdx = (this._searchIdx + dir + this._searchMatches.length) % this._searchMatches.length;
    this._highlightCurrentMatch();
  }

  _highlightCurrentMatch() {
    const span = this._searchMatches[this._searchIdx];
    if (!span) return;

    document.querySelectorAll('.pdf-search-current').forEach(el => el.classList.remove('pdf-search-current'));
    span.classList.add('pdf-search-current');
    span.scrollIntoView({ behavior: 'smooth', block: 'center' });

    const pageNum = parseInt(span.dataset.page);
    if (pageNum) {
      this.currentPage = pageNum;
      this._updatePageInfo();
      this._highlightThumbnail(pageNum);
    }

    const countEl = document.getElementById('pdf-search-count');
    if (countEl) countEl.textContent = `${this._searchIdx + 1} / ${this._searchMatches.length}`;
  }

  _clearSearchHighlights() {
    document.querySelectorAll('.pdf-search-match').forEach(el => el.classList.remove('pdf-search-match'));
    document.querySelectorAll('.pdf-search-current').forEach(el => el.classList.remove('pdf-search-current'));
    this._searchMatches = [];
    this._searchIdx = -1;
  }

  // ==========================================
  // ROTATE (pdf-lib)
  // ==========================================

  async _rotateCurrentPage() {
    const lib = PDFLib();
    if (!lib || !this.pdfBytes) { alert('Load a PDF first.'); return; }

    try {
      const pdfDoc = await lib.PDFDocument.load(this.pdfBytes);
      const pages = pdfDoc.getPages();
      const page = pages[this.currentPage - 1];
      if (!page) return;

      const current = page.getRotation().angle;
      page.setRotation(lib.degrees((current + 90) % 360));

      this.pdfBytes = await pdfDoc.save();
      this.rotations[this.currentPage] = ((this.rotations[this.currentPage] || 0) + 90) % 360;
      await this._loadPDF(this.pdfBytes.buffer);
    } catch (e) {
      alert('Rotate failed: ' + e.message);
    }
  }

  // ==========================================
  // MERGE PDFs (pdf-lib)
  // ==========================================

  async _mergeDialog() {
    const lib = PDFLib();
    if (!lib) { alert('pdf-lib not loaded.'); return; }
    if (!this.pdfBytes) { alert('Load a PDF first, then merge additional files into it.'); return; }

    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.pdf';
    input.multiple = true;
    input.onchange = async () => {
      if (!input.files || input.files.length === 0) return;
      try {
        const mergedPdf = await lib.PDFDocument.load(this.pdfBytes);

        for (const file of input.files) {
          const buf = await file.arrayBuffer();
          const srcPdf = await lib.PDFDocument.load(buf);
          const pages = await mergedPdf.copyPages(srcPdf, srcPdf.getPageIndices());
          pages.forEach(p => mergedPdf.addPage(p));
        }

        this.pdfBytes = await mergedPdf.save();
        await this._loadPDF(this.pdfBytes.buffer);
        this._updateFilename();
      } catch (e) {
        alert('Merge failed: ' + e.message);
      }
    };
    input.click();
  }

  // ==========================================
  // SPLIT / EXTRACT PAGES (pdf-lib)
  // ==========================================

  async _splitDialog() {
    const lib = PDFLib();
    if (!lib || !this.pdfBytes) { alert('Load a PDF first.'); return; }

    const range = prompt(`Extract pages (e.g. "1-3" or "1,3,5"):\nTotal pages: ${this.totalPages}`);
    if (!range) return;

    try {
      const srcPdf = await lib.PDFDocument.load(this.pdfBytes);
      const newPdf = await lib.PDFDocument.create();

      // Parse range
      const pageNums = [];
      range.split(',').forEach(part => {
        const trimmed = part.trim();
        if (trimmed.includes('-')) {
          const [start, end] = trimmed.split('-').map(n => parseInt(n.trim()));
          for (let i = start; i <= end && i <= this.totalPages; i++) {
            if (i >= 1) pageNums.push(i - 1); // 0-indexed
          }
        } else {
          const n = parseInt(trimmed);
          if (n >= 1 && n <= this.totalPages) pageNums.push(n - 1);
        }
      });

      if (pageNums.length === 0) { alert('Invalid page range.'); return; }

      const copied = await newPdf.copyPages(srcPdf, pageNums);
      copied.forEach(p => newPdf.addPage(p));

      const bytes = await newPdf.save();
      const blob = new Blob([bytes], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${this.fileName.replace('.pdf', '')}_pages_${range}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      alert('Split failed: ' + e.message);
    }
  }

  // ==========================================
  // DELETE PAGE (pdf-lib)
  // ==========================================

  async _deleteCurrentPage() {
    const lib = PDFLib();
    if (!lib || !this.pdfBytes) return;
    if (this.totalPages <= 1) { alert('Cannot delete the only page.'); return; }
    if (!confirm(`Delete page ${this.currentPage}?`)) return;

    try {
      const pdfDoc = await lib.PDFDocument.load(this.pdfBytes);
      pdfDoc.removePage(this.currentPage - 1);
      this.pdfBytes = await pdfDoc.save();
      if (this.currentPage > 1) this.currentPage--;
      await this._loadPDF(this.pdfBytes.buffer);
    } catch (e) {
      alert('Delete failed: ' + e.message);
    }
  }

  // ==========================================
  // STAMPS (Approved, Draft, Confidential, etc.)
  // ==========================================

  _showStampPicker() {
    const existing = document.getElementById('stamp-modal');
    if (existing) { existing.remove(); return; }

    const stamps = [
      { label: 'APPROVED', color: '#16a34a', bg: '#dcfce7' },
      { label: 'REJECTED', color: '#dc2626', bg: '#fee2e2' },
      { label: 'DRAFT', color: '#9333ea', bg: '#f3e8ff' },
      { label: 'CONFIDENTIAL', color: '#dc2626', bg: '#fee2e2' },
      { label: 'REVIEWED', color: '#2563eb', bg: '#dbeafe' },
      { label: 'FINAL', color: '#059669', bg: '#d1fae5' },
      { label: 'COPY', color: '#6b7280', bg: '#f3f4f6' },
      { label: 'VOID', color: '#ef4444', bg: '#fef2f2' },
    ];

    const modal = document.createElement('div');
    modal.id = 'stamp-modal';
    modal.className = 'pdf-modal-overlay';
    const panel = document.createElement('div');
    panel.className = 'pdf-modal-panel';
    panel.innerHTML = `
      <div class="pdf-modal-header"><strong>Choose Stamp</strong><button class="pdf-modal-close">\u2715</button></div>
      <div class="pdf-stamp-grid">${stamps.map(s =>
        `<button class="pdf-stamp-option" data-label="${s.label}" data-color="${s.color}" style="border:2px solid ${s.color};color:${s.color};background:${s.bg}">${s.label}</button>`
      ).join('')}</div>
      <div style="margin-top:12px;font-size:12px;color:#888">Click a stamp, then click on the PDF to place it</div>
    `;
    modal.appendChild(panel);
    modal.addEventListener('click', (e) => { if (e.target === modal) modal.remove(); });
    panel.querySelector('.pdf-modal-close').addEventListener('click', () => modal.remove());
    panel.querySelectorAll('.pdf-stamp-option').forEach(btn => {
      btn.addEventListener('click', () => {
        const label = btn.dataset.label;
        const color = btn.dataset.color;
        this._pendingStamp = { label, color };
        this.activeTool = 'stamp';
        document.querySelectorAll('.pdf-annotation-layer').forEach(l => l.style.pointerEvents = 'auto');
        const viewer = document.getElementById('pdf-viewer');
        if (viewer) viewer.style.cursor = 'copy';
        modal.remove();
      });
    });
    document.body.appendChild(modal);
  }

  _placeStamp(pageNum, pos, canvas) {
    if (!this._pendingStamp) return;
    const { label, color } = this._pendingStamp;
    const ctx = canvas.getContext('2d');
    ctx.save();
    ctx.strokeStyle = color;
    ctx.lineWidth = 3;
    ctx.fillStyle = color;
    ctx.font = 'bold 28px sans-serif';
    ctx.globalAlpha = 0.6;
    const w = ctx.measureText(label).width + 24;
    const h = 40;
    const x = pos.x - w / 2;
    const y = pos.y - h / 2;
    ctx.strokeRect(x, y, w, h);
    ctx.fillText(label, x + 12, y + 29);
    ctx.restore();

    // Save as annotation for redraw
    if (!this.annotations[pageNum]) this.annotations[pageNum] = [];
    const stampAnnotation = { type: 'stamp', label, color, x, y, w, h };
    this.annotations[pageNum].push(stampAnnotation);
    this._pushUndo({ type: 'stamp', pageNum, annotation: stampAnnotation });

    this._pendingStamp = null;
    this.activeTool = null;
    document.querySelectorAll('.pdf-annotation-layer').forEach(l => l.style.pointerEvents = 'none');
    const viewer = document.getElementById('pdf-viewer');
    if (viewer) viewer.style.cursor = '';
  }

  // ==========================================
  // REDACTION
  // ==========================================

  _addRedaction(pageNum, start, end, canvas) {
    const x = Math.min(start.x, end.x);
    const y = Math.min(start.y, end.y);
    const w = Math.abs(end.x - start.x);
    const h = Math.abs(end.y - start.y);
    if (w < 5 || h < 5) return;

    const redaction = { x, y, w, h };
    if (!this._redactions[pageNum]) this._redactions[pageNum] = [];
    this._redactions[pageNum].push(redaction);
    this._pushUndo({ type: 'redaction', pageNum, redaction });
    this._redrawAnnotations(pageNum, canvas);
  }

  // ==========================================
  // FORM FIELD DETECTION
  // ==========================================

  async _detectFormFields() {
    if (!this.pdfDoc) { alert('Load a PDF first.'); return; }

    // Clear existing form overlays
    document.querySelectorAll('.pdf-form-field').forEach(el => el.remove());

    for (let i = 1; i <= this.totalPages; i++) {
      const page = await this.pdfDoc.getPage(i);
      const annotations = await page.getAnnotations();
      const viewport = page.getViewport({ scale: this.scale, rotation: this.rotations[i] || 0 });
      const pageDiv = document.querySelector(`.pdf-page[data-page="${i}"]`);
      if (!pageDiv) continue;

      for (const annot of annotations) {
        if (!annot.rect || !annot.subtype) continue;

        // Convert PDF rect to viewport coordinates
        const [x1, y1, x2, y2] = window.pdfjsLib.Util.normalizeRect(
          viewport.convertToViewportRectangle(annot.rect)
        );

        if (annot.subtype === 'Widget') {
          const fieldType = annot.fieldType;
          let el;

          if (fieldType === 'Tx') {
            // Text input
            el = document.createElement('input');
            el.type = annot.multiLine ? 'textarea' : 'text';
            el.className = 'pdf-form-field pdf-form-text';
            el.value = annot.fieldValue || '';
            el.placeholder = annot.alternativeText || annot.fieldName || '';
          } else if (fieldType === 'Btn') {
            if (annot.checkBox) {
              el = document.createElement('input');
              el.type = 'checkbox';
              el.className = 'pdf-form-field pdf-form-checkbox';
              el.checked = annot.fieldValue === annot.exportValue;
            } else if (annot.radioButton) {
              el = document.createElement('input');
              el.type = 'radio';
              el.name = annot.fieldName;
              el.className = 'pdf-form-field pdf-form-radio';
              el.checked = annot.fieldValue === annot.buttonValue;
            } else {
              continue;
            }
          } else if (fieldType === 'Ch') {
            el = document.createElement('select');
            el.className = 'pdf-form-field pdf-form-select';
            (annot.options || []).forEach(opt => {
              const o = document.createElement('option');
              o.value = opt.exportValue || opt.displayValue;
              o.textContent = opt.displayValue;
              if (annot.fieldValue === o.value) o.selected = true;
              el.appendChild(o);
            });
          } else {
            continue;
          }

          if (el) {
            el.style.cssText = `
              position:absolute;
              left:${x1}px;top:${y1}px;
              width:${x2 - x1}px;height:${y2 - y1}px;
            `;
            el.dataset.fieldName = annot.fieldName || '';
            el.dataset.page = i;
            pageDiv.appendChild(el);
          }
        }
      }
    }

    // Flash the forms button to indicate detection ran
    const btn = document.getElementById('pdf-form-fill');
    if (btn) { btn.classList.add('active'); setTimeout(() => btn.classList.remove('active'), 2000); }
  }

  // ==========================================
  // PAGE REORDER (drag thumbnails)
  // ==========================================

  _toggleReorderMode() {
    this._reorderMode = !this._reorderMode;
    const btn = document.getElementById('pdf-reorder');
    if (btn) btn.classList.toggle('active', this._reorderMode);

    const sidebar = document.getElementById('pdf-thumbnails');
    if (!sidebar) return;

    if (this._reorderMode) {
      sidebar.classList.add('reorder-mode');
      const thumbs = sidebar.querySelectorAll('.pdf-thumbnail');
      thumbs.forEach(thumb => {
        thumb.draggable = true;
        thumb.addEventListener('dragstart', this._onThumbDragStart);
        thumb.addEventListener('dragover', this._onThumbDragOver);
        thumb.addEventListener('drop', (e) => this._onThumbDrop(e));
        thumb.addEventListener('dragend', this._onThumbDragEnd);
      });
    } else {
      sidebar.classList.remove('reorder-mode');
      sidebar.querySelectorAll('.pdf-thumbnail').forEach(t => t.draggable = false);
    }
  }

  _onThumbDragStart(e) {
    e.dataTransfer.setData('text/plain', e.currentTarget.dataset.page);
    e.currentTarget.classList.add('dragging');
  }

  _onThumbDragOver(e) {
    e.preventDefault();
    e.currentTarget.classList.add('drag-over');
  }

  _onThumbDragEnd(e) {
    document.querySelectorAll('.pdf-thumbnail').forEach(t => {
      t.classList.remove('dragging', 'drag-over');
    });
  }

  async _onThumbDrop(e) {
    e.preventDefault();
    const fromPage = parseInt(e.dataTransfer.getData('text/plain'));
    const toPage = parseInt(e.currentTarget.dataset.page);
    if (fromPage === toPage || !fromPage || !toPage) return;

    document.querySelectorAll('.pdf-thumbnail').forEach(t => t.classList.remove('drag-over'));

    const lib = PDFLib();
    if (!lib || !this.pdfBytes) return;

    try {
      const srcPdf = await lib.PDFDocument.load(this.pdfBytes);
      const newPdf = await lib.PDFDocument.create();

      const pageCount = srcPdf.getPageCount();
      const order = Array.from({ length: pageCount }, (_, i) => i);

      // Move fromPage to toPage position
      const fromIdx = fromPage - 1;
      const toIdx = toPage - 1;
      order.splice(fromIdx, 1);
      order.splice(toIdx, 0, fromIdx);

      const copied = await newPdf.copyPages(srcPdf, order);
      copied.forEach(p => newPdf.addPage(p));

      this.pdfBytes = await newPdf.save();
      await this._loadPDF(this.pdfBytes.buffer);
    } catch (err) {
      alert('Reorder failed: ' + err.message);
    }
  }

  // ==========================================
  // CROP PAGES
  // ==========================================

  async _cropDialog() {
    const lib = PDFLib();
    if (!lib || !this.pdfBytes) { alert('Load a PDF first.'); return; }

    const input = prompt(
      'Crop margins (pixels to remove from each edge):\n' +
      'Format: top, right, bottom, left\n' +
      'Example: 50, 30, 50, 30\n' +
      'Apply to: current page only'
    );
    if (!input) return;

    const parts = input.split(',').map(s => parseFloat(s.trim()));
    if (parts.length !== 4 || parts.some(isNaN)) { alert('Invalid format. Use: top, right, bottom, left'); return; }
    const [top, right, bottom, left] = parts;

    try {
      const pdfDoc = await lib.PDFDocument.load(this.pdfBytes);
      const page = pdfDoc.getPages()[this.currentPage - 1];
      if (!page) return;

      const { width, height } = page.getSize();
      page.setCropBox(left, bottom, width - right, height - top);

      this.pdfBytes = await pdfDoc.save();
      await this._loadPDF(this.pdfBytes.buffer);
    } catch (e) {
      alert('Crop failed: ' + e.message);
    }
  }

  // ==========================================
  // WATERMARK
  // ==========================================

  async _watermarkDialog() {
    const lib = PDFLib();
    if (!lib || !this.pdfBytes) { alert('Load a PDF first.'); return; }

    const existing = document.getElementById('watermark-modal');
    if (existing) existing.remove();

    const modal = document.createElement('div');
    modal.id = 'watermark-modal';
    modal.className = 'pdf-modal-overlay';
    const panel = document.createElement('div');
    panel.className = 'pdf-modal-panel';
    panel.innerHTML = `
      <div class="pdf-modal-header"><strong>Add Watermark</strong><button class="pdf-modal-close">\u2715</button></div>
      <div style="display:flex;flex-direction:column;gap:10px;margin-top:12px">
        <label style="font-size:13px">Text:<input id="wm-text" type="text" value="CONFIDENTIAL" style="width:100%;padding:6px 10px;border:1px solid #ddd;border-radius:4px;margin-top:4px;font-size:13px"></label>
        <div style="display:flex;gap:10px">
          <label style="font-size:13px;flex:1">Size:<input id="wm-size" type="number" value="60" min="10" max="200" style="width:100%;padding:6px;border:1px solid #ddd;border-radius:4px;margin-top:4px"></label>
          <label style="font-size:13px;flex:1">Opacity:<input id="wm-opacity" type="number" value="15" min="1" max="100" step="5" style="width:100%;padding:6px;border:1px solid #ddd;border-radius:4px;margin-top:4px">%</label>
        </div>
        <div style="display:flex;gap:10px">
          <label style="font-size:13px;flex:1">Color:<input id="wm-color" type="color" value="#888888" style="width:100%;height:32px;border:1px solid #ddd;border-radius:4px;margin-top:4px;cursor:pointer"></label>
          <label style="font-size:13px;flex:1">Angle:<input id="wm-angle" type="number" value="45" min="0" max="360" style="width:100%;padding:6px;border:1px solid #ddd;border-radius:4px;margin-top:4px">&deg;</label>
        </div>
        <button id="wm-apply" style="padding:10px;background:linear-gradient(145deg,#1e40af,#1e3a8a);color:white;border:none;border-radius:6px;font-weight:600;cursor:pointer;margin-top:4px">Apply to All Pages</button>
      </div>
    `;
    modal.appendChild(panel);
    modal.addEventListener('click', (e) => { if (e.target === modal) modal.remove(); });
    panel.querySelector('.pdf-modal-close').addEventListener('click', () => modal.remove());
    panel.querySelector('#wm-apply').addEventListener('click', async () => {
      const text = document.getElementById('wm-text').value.trim();
      const size = parseInt(document.getElementById('wm-size').value);
      const opacity = parseInt(document.getElementById('wm-opacity').value) / 100;
      const hex = document.getElementById('wm-color').value;
      const angle = parseInt(document.getElementById('wm-angle').value);
      if (!text) return;

      const r = parseInt(hex.slice(1, 3), 16) / 255;
      const g = parseInt(hex.slice(3, 5), 16) / 255;
      const b = parseInt(hex.slice(5, 7), 16) / 255;

      try {
        const pdfDoc = await lib.PDFDocument.load(this.pdfBytes);
        const font = await pdfDoc.embedFont(lib.StandardFonts.HelveticaBold);
        const pages = pdfDoc.getPages();

        for (const page of pages) {
          const { width, height } = page.getSize();
          const textWidth = font.widthOfTextAtSize(text, size);
          page.drawText(text, {
            x: width / 2 - textWidth / 2,
            y: height / 2,
            size,
            font,
            color: lib.rgb(r, g, b),
            opacity,
            rotate: lib.degrees(angle),
          });
        }

        this.pdfBytes = await pdfDoc.save();
        await this._loadPDF(this.pdfBytes.buffer);
        modal.remove();
      } catch (e) {
        alert('Watermark failed: ' + e.message);
      }
    });
    document.body.appendChild(modal);
  }

  // ==========================================
  // PAGE NUMBERS
  // ==========================================

  async _addPageNumbers() {
    const lib = PDFLib();
    if (!lib || !this.pdfBytes) { alert('Load a PDF first.'); return; }

    const pos = prompt('Page number position:\n1 = Bottom center\n2 = Bottom right\n3 = Top center\n4 = Top right', '1');
    if (!pos) return;

    try {
      const pdfDoc = await lib.PDFDocument.load(this.pdfBytes);
      const font = await pdfDoc.embedFont(lib.StandardFonts.Helvetica);
      const pages = pdfDoc.getPages();

      pages.forEach((page, i) => {
        const { width, height } = page.getSize();
        const text = `${i + 1}`;
        const textWidth = font.widthOfTextAtSize(text, 10);
        let x, y;

        switch (pos) {
          case '2': x = width - 40; y = 20; break;
          case '3': x = width / 2 - textWidth / 2; y = height - 20; break;
          case '4': x = width - 40; y = height - 20; break;
          default:  x = width / 2 - textWidth / 2; y = 20; break;
        }

        page.drawText(text, { x, y, size: 10, font, color: lib.rgb(0.4, 0.4, 0.4) });
      });

      this.pdfBytes = await pdfDoc.save();
      await this._loadPDF(this.pdfBytes.buffer);
    } catch (e) {
      alert('Page numbers failed: ' + e.message);
    }
  }

  // ==========================================
  // FLATTEN ANNOTATIONS
  // ==========================================

  async _flattenAnnotations() {
    if (!this.pdfBytes) { alert('Load a PDF first.'); return; }
    if (!confirm('Flatten all annotations permanently into the PDF?\nThis cannot be undone.')) return;

    const lib = PDFLib();
    if (!lib) return;

    try {
      const pdfDoc = await lib.PDFDocument.load(this.pdfBytes);
      const font = await pdfDoc.embedFont(lib.StandardFonts.Helvetica);
      const fontBold = await pdfDoc.embedFont(lib.StandardFonts.HelveticaBold);
      const pages = pdfDoc.getPages();

      for (let pageNum = 1; pageNum <= this.totalPages; pageNum++) {
        const page = pages[pageNum - 1];
        if (!page) continue;
        const { width, height } = page.getSize();
        const scaleFactor = width / (width * this.scale); // approximate

        // Flatten redactions
        const redactions = this._redactions[pageNum] || [];
        for (const r of redactions) {
          page.drawRectangle({
            x: r.x * scaleFactor,
            y: height - (r.y + r.h) * scaleFactor,
            width: r.w * scaleFactor,
            height: r.h * scaleFactor,
            color: lib.rgb(0, 0, 0),
          });
        }

        // Flatten stamp annotations
        const stampAnnots = (this.annotations[pageNum] || []).filter(a => a.type === 'stamp');
        for (const s of stampAnnots) {
          page.drawText(s.label, {
            x: (s.x + 12) * scaleFactor,
            y: height - (s.y + 29) * scaleFactor,
            size: 28 * scaleFactor,
            font: fontBold,
            color: lib.rgb(
              parseInt(s.color.slice(1,3),16)/255,
              parseInt(s.color.slice(3,5),16)/255,
              parseInt(s.color.slice(5,7),16)/255
            ),
            opacity: 0.6,
          });
        }
      }

      this.pdfBytes = await pdfDoc.save();
      // Clear in-memory annotations
      this._redactions = {};
      this.annotations = {};
      this._drawPaths = {};
      this._highlights = {};
      this._notes = {};
      this._textAnnotations = {};

      await this._loadPDF(this.pdfBytes.buffer);
    } catch (e) {
      alert('Flatten failed: ' + e.message);
    }
  }

  // ==========================================
  // BOOKMARKS / OUTLINE
  // ==========================================

  async _toggleBookmarks() {
    const panel = document.getElementById('pdf-bookmarks-panel');
    if (!panel) return;

    const isVisible = panel.style.display !== 'none';
    panel.style.display = isVisible ? 'none' : 'flex';

    const btn = document.getElementById('pdf-bookmarks');
    if (btn) btn.classList.toggle('active', !isVisible);

    if (!isVisible && this.pdfDoc) {
      await this._loadBookmarks();
    }
  }

  async _loadBookmarks() {
    const list = document.getElementById('pdf-bookmarks-list');
    if (!list || !this.pdfDoc) return;
    list.innerHTML = '';

    try {
      const outline = await this.pdfDoc.getOutline();
      if (!outline || outline.length === 0) {
        list.innerHTML = '<div style="padding:16px;color:#888;font-size:13px">No bookmarks found in this PDF.</div>';
        return;
      }
      this._renderBookmarkItems(outline, list, 0);
    } catch (e) {
      list.innerHTML = '<div style="padding:16px;color:#888;font-size:13px">Could not load bookmarks.</div>';
    }
  }

  _renderBookmarkItems(items, container, depth) {
    for (const item of items) {
      const el = document.createElement('div');
      el.className = 'pdf-bookmark-item';
      el.style.paddingLeft = (12 + depth * 16) + 'px';
      el.textContent = item.title;
      el.addEventListener('click', async () => {
        if (item.dest) {
          try {
            let dest = item.dest;
            if (typeof dest === 'string') {
              dest = await this.pdfDoc.getDestination(dest);
            }
            if (dest) {
              const ref = dest[0];
              const pageIndex = await this.pdfDoc.getPageIndex(ref);
              this._goToPage(pageIndex + 1);
            }
          } catch (e) {
            // Fallback: try page number from item
            if (item.pageNumber) this._goToPage(item.pageNumber);
          }
        }
      });
      container.appendChild(el);

      if (item.items && item.items.length > 0) {
        this._renderBookmarkItems(item.items, container, depth + 1);
      }
    }
  }

  // ==========================================
  // DOCUMENT PROPERTIES
  // ==========================================

  async _showProperties() {
    if (!this.pdfDoc) { alert('Load a PDF first.'); return; }

    const existing = document.getElementById('props-modal');
    if (existing) existing.remove();

    let metadata = {};
    let info = {};
    try {
      const data = await this.pdfDoc.getMetadata();
      metadata = data.metadata?.getAll?.() || {};
      info = data.info || {};
    } catch (e) {}

    const page1 = await this.pdfDoc.getPage(1);
    const vp = page1.getViewport({ scale: 1 });
    const fileSize = this.pdfBytes ? (this.pdfBytes.length / 1024).toFixed(1) : '?';
    const fileSizeStr = fileSize > 1024 ? `${(fileSize / 1024).toFixed(1)} MB` : `${fileSize} KB`;

    const props = [
      ['File Name', this.fileName],
      ['File Size', fileSizeStr],
      ['Pages', this.totalPages],
      ['Page Size', `${Math.round(vp.width)} x ${Math.round(vp.height)} pts`],
      ['Title', info.Title || metadata['dc:title'] || '—'],
      ['Author', info.Author || metadata['dc:creator'] || '—'],
      ['Subject', info.Subject || metadata['dc:description'] || '—'],
      ['Creator', info.Creator || metadata['xmp:CreatorTool'] || '—'],
      ['Producer', info.Producer || '—'],
      ['Created', info.CreationDate ? new Date(info.CreationDate).toLocaleDateString() : '—'],
      ['Modified', info.ModDate ? new Date(info.ModDate).toLocaleDateString() : '—'],
      ['PDF Version', info.PDFFormatVersion || '—'],
    ];

    const modal = document.createElement('div');
    modal.id = 'props-modal';
    modal.className = 'pdf-modal-overlay';
    const panel = document.createElement('div');
    panel.className = 'pdf-modal-panel';
    panel.innerHTML = `
      <div class="pdf-modal-header"><strong>Document Properties</strong><button class="pdf-modal-close">\u2715</button></div>
      <table class="pdf-props-table">${props.map(([k, v]) =>
        `<tr><td class="pdf-prop-key">${k}</td><td class="pdf-prop-val">${v}</td></tr>`
      ).join('')}</table>
    `;
    modal.appendChild(panel);
    modal.addEventListener('click', (e) => { if (e.target === modal) modal.remove(); });
    panel.querySelector('.pdf-modal-close').addEventListener('click', () => modal.remove());
    document.body.appendChild(modal);
  }

  // ==========================================
  // DARK MODE
  // ==========================================

  _toggleDarkMode() {
    this._darkMode = !this._darkMode;
    const viewer = document.getElementById('pdf-viewer');
    if (viewer) {
      viewer.classList.toggle('pdf-dark-mode', this._darkMode);
    }
    const btn = document.getElementById('pdf-dark-mode');
    if (btn) btn.classList.toggle('active', this._darkMode);
  }

  // ==========================================
  // PRESENTATION MODE
  // ==========================================

  _presentationMode() {
    if (!this.pdfDoc) { alert('Load a PDF first.'); return; }

    const container = document.getElementById('pdf-viewer-container');
    if (!container) return;

    if (document.fullscreenElement) {
      document.exitFullscreen();
      return;
    }

    container.requestFullscreen().then(() => {
      container.classList.add('pdf-presentation');
      // Fit page to screen
      this._fitPage();
      // ESC listener to clean up
      const onExit = () => {
        if (!document.fullscreenElement) {
          container.classList.remove('pdf-presentation');
          document.removeEventListener('fullscreenchange', onExit);
        }
      };
      document.addEventListener('fullscreenchange', onExit);
    }).catch(() => {
      // Fallback: just enlarge
      container.classList.toggle('pdf-presentation');
    });
  }

  // ==========================================
  // PRINT & DOWNLOAD
  // ==========================================

  _print() {
    if (!this.pdfBytes) { alert('No PDF loaded.'); return; }
    const blob = new Blob([this.pdfBytes], { type: 'application/pdf' });
    const url = URL.createObjectURL(blob);
    const win = window.open(url);
    if (win) {
      win.addEventListener('load', () => { win.print(); });
    }
  }

  async _download() {
    if (!this.pdfBytes) { alert('No PDF loaded.'); return; }

    // Apply text edits to the PDF before saving
    const hasEdits = Object.keys(this._textEdits).some(p => Object.keys(this._textEdits[p]).length > 0);
    let bytesToSave = this.pdfBytes;

    if (hasEdits) {
      try {
        bytesToSave = await this._applyTextEdits();
      } catch (e) {
        console.error('Failed to apply text edits:', e);
        // Fall back to original bytes
      }
    }

    const blob = new Blob([bytesToSave], { type: 'application/pdf' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = this.fileName;
    a.click();
    URL.revokeObjectURL(url);
  }

  async _applyTextEdits() {
    const lib = PDFLib();
    if (!lib) return this.pdfBytes;

    const pdfDoc = await lib.PDFDocument.load(this.pdfBytes);
    const pages = pdfDoc.getPages();
    const font = await pdfDoc.embedFont(lib.StandardFonts.Helvetica);

    for (const [pageNumStr, edits] of Object.entries(this._textEdits)) {
      const pageNum = parseInt(pageNumStr);
      const page = pages[pageNum - 1];
      if (!page) continue;

      for (const [itemIdxStr, edit] of Object.entries(edits)) {
        const entry = edit.entry;
        if (!entry || !entry.item?.transform) continue;

        const item = entry.item;
        const tx = item.transform;
        const fontSize = Math.sqrt(tx[0] * tx[0] + tx[1] * tx[1]);
        const x = tx[4];
        const y = tx[5];
        const textWidth = font.widthOfTextAtSize(item.str, fontSize) + 6;

        // White out original text item
        page.drawRectangle({
          x: x - 1,
          y: y - 2,
          width: textWidth,
          height: fontSize + 4,
          color: lib.rgb(1, 1, 1),
          borderWidth: 0,
        });

        // Draw replacement text at the same position
        page.drawText(edit.edited, {
          x,
          y,
          size: fontSize,
          font,
          color: lib.rgb(0, 0, 0),
        });
      }
    }

    return await pdfDoc.save();
  }

  // ==========================================
  // UI HELPERS
  // ==========================================

  _hideEmptyState() {
    const empty = document.getElementById('pdf-empty-state');
    if (empty) empty.style.display = 'none';
    const viewer = document.getElementById('pdf-viewer');
    if (viewer) viewer.style.display = '';
    const thumbs = document.getElementById('pdf-thumbnails');
    if (thumbs) thumbs.style.display = '';
  }

  _updateFilename() {
    const el = document.getElementById('pdf-filename');
    if (el) el.textContent = this.fileName;
    const sizeEl = document.getElementById('pdf-file-size');
    if (sizeEl && this.pdfBytes) {
      const kb = (this.pdfBytes.length / 1024).toFixed(1);
      sizeEl.textContent = kb > 1024 ? `${(kb / 1024).toFixed(1)} MB` : `${kb} KB`;
    }
  }
}
